import React, { useState, useEffect, useCallback } from "react";
import { X, ShoppingCart, ArrowRight, ChevronDown, ChevronUp } from "react-feather";
import { FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { base_api_url } from "../../../environment.jsx";

const CartSidePanel = ({ isOpen, onClose, restauId, Table, onCartUpdate }) => {
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();

  const [recaporder, setRecaporder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cartQuantity, setCartQuantity] = useState(0);
  const [reference, setReference] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  const devise =
    JSON.parse(localStorage.getItem("restaurant"))?.Restaurant?.devise ??
    JSON.parse(localStorage.getItem("restaurant"))?.restaurant?.devise;

  // Fetch order details whenever the panel opens
  const fetchOrderDetails = useCallback(async () => {
    setLoading(true);
    try {
      const client = JSON.parse(sessionStorage.getItem("client"));
      if (!client?.reference) {
        setRecaporder([]);
        setCartQuantity(0);
        setLoading(false);
        return;
      }

      const response = await axios.get(`${base_api_url}api/commande/${client.reference}`);
      if (response.status === 200 || response.status === 201) {
        setReference(response.data.reference);

        // Helper: handles both plain number and {source, parsedValue} object
        const parsePrice = (v) => {
          if (v === null || v === undefined) return 0;
          if (typeof v === "number") return v;
          if (typeof v === "object") return parseFloat(v.parsedValue ?? v.source) || 0;
          return parseFloat(v) || 0;
        };

        const transformedData =
          response.data.details?.map((detail) => {
            const article = detail.article?.[0];
            // Map complements from the real API field: detail_commande_complements
            const complements = (detail.detail_commande_complements || []).map((c) => ({
              nom: c.complement_nom || "Complément",
              groupe: c.complement_group_nom || "",
              id_complement_group: c.id_complement_group,
              id_complement: c.id_complement,
              quantite: c.quantite || 1,
              prix: parsePrice(c.prix),
              nbre_gratuit: c.nbre_gratuit || 0,
            }));
            return {
              id_details: detail.id_details,
              id_article: article?.id_article,
              title: article?.nom || "Article inconnu",
              underTitle: article?.description || "",
              prix: parsePrice(detail.prix_unitaire),
              quantity: detail.quantite,
              prix_total: detail.prix_total,
              nb_complement: article?.nb_complement,
              commentaire: detail.commentaire,
              complements,
              image_url:
                article?.image_url && !article?.image_url.startsWith("data:image")
                  ? `${base_api_url}api/recuperer/${article.image_url}`
                  : article?.image_url,
            };
          }) || [];

        setRecaporder(transformedData);

        const totalQty =
          response.data.details?.reduce((t, d) => t + (d.quantite || 0), 0) || 0;
        setCartQuantity(totalQty);

        // Helper function to compute total price of recaporder items including dynamic grouped complement surcharges
        const getOrderTotal = (items) => {
          return items.reduce((t, i) => {
            const complementsByGroup = {};
            (i.complements || []).forEach((c) => {
              const groupId = c.id_complement_group || c.groupe;
              if (!complementsByGroup[groupId]) {
                complementsByGroup[groupId] = {
                  nbre_gratuit: c.nbre_gratuit || 0,
                  items: []
                };
              }
              complementsByGroup[groupId].items.push({ ...c });
            });

            let complementsAdditional = 0;
            Object.keys(complementsByGroup).forEach((groupId) => {
              const group = complementsByGroup[groupId];
              const totalFreeSlots = group.nbre_gratuit * i.quantity;
              group.items.sort((a, b) => b.prix - a.prix);
              
              let freeSlotsRemaining = totalFreeSlots;
              group.items.forEach((c) => {
                let qty = c.quantite;
                let freeUsed = Math.min(qty, freeSlotsRemaining);
                let paidQty = qty - freeUsed;
                freeSlotsRemaining -= freeUsed;
                complementsAdditional += paidQty * c.prix;
              });
            });
            return t + i.prix * i.quantity + complementsAdditional;
          }, 0);
        };

        // Sync localStorage — include complement surcharges in total
        const totalPrice = getOrderTotal(transformedData);
        localStorage.setItem("total", totalPrice);
        localStorage.setItem("quantite", totalQty);

        if (onCartUpdate) onCartUpdate(totalQty);
      }
    } catch (err) {
      console.error("CartSidePanel fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [onCartUpdate]);

  useEffect(() => {
    if (isOpen) fetchOrderDetails();
  }, [isOpen, fetchOrderDetails]);

  // ---- Quantity change removed: items must be re-added via modal ----

  // ---- Delete ----
  const handleDelete = async (id_article) => {
    try {
      const res = await axios.put(`${base_api_url}api/rmdetailscmd/${id_article}/${reference}`);
      if (res.status === 200) {
        MySwal.fire({
          icon: "success",
          text: "Article supprimé !",
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 2000,
          customClass: { popup: "pixel-toast" },
        });
        fetchOrderDetails();
      }
    } catch (e) {
      MySwal.fire({
        icon: "error",
        text: "Impossible de supprimer l'article.",
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        customClass: { popup: "pixel-toast" },
      });
    }
  };

  // Total includes complement surcharges using the helper
  const calculateTotal = () => {
    return recaporder.reduce((t, i) => {
      const groupSums = {};
      (i.complements || []).forEach((c) => {
        const groupId = c.id_complement_group || c.groupe;
        if (!groupSums[groupId]) {
          groupSums[groupId] = {
            nbre_gratuit: c.nbre_gratuit || 0,
            items: []
          };
        }
        groupSums[groupId].items.push({ ...c });
      });

      let complementsAdditional = 0;
      Object.keys(groupSums).forEach((groupId) => {
        const group = groupSums[groupId];
        const totalFreeSlots = group.nbre_gratuit * i.quantity;
        group.items.sort((a, b) => b.prix - a.prix);
        
        let freeSlotsRemaining = totalFreeSlots;
        group.items.forEach((c) => {
          let qty = c.quantite;
          let freeUsed = Math.min(qty, freeSlotsRemaining);
          let paidQty = qty - freeUsed;
          freeSlotsRemaining -= freeUsed;
          complementsAdditional += paidQty * c.prix;
        });
      });
      return t + i.prix * i.quantity + complementsAdditional;
    }, 0);
  };

  const handleCheckout = () => {
    const total = calculateTotal();
    localStorage.setItem("total", total);
    localStorage.setItem("quantite", cartQuantity);
    onClose();
    navigate(`/specific-instruction?r=${restauId}&t=${Table}`);
  };

  // ---- Styles ----
  const overlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    zIndex: 9998,
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? "auto" : "none",
    transition: "opacity 0.3s ease",
  };

  const panelStyle = {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    height: "100dvh", // Modern browsers: excludes toolbar
    width: "min(420px, 100vw)",
    backgroundColor: "#fff",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    transform: isOpen ? "translateX(0)" : "translateX(100%)",
    transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
    borderRadius: window.innerWidth < 420 ? "0" : "16px 0 0 16px", // No radius if full width
  };

  return (
    <>
      {/* Overlay */}
      <div style={overlayStyle} onClick={onClose} />

      {/* Panel */}
      <div style={panelStyle}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 20px 16px",
            borderBottom: "1px solid #f0f0f0",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ShoppingCart size={22} color="#E7592C" />
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#001b44" }}>
              Mon Panier
            </h2>
            {cartQuantity > 0 && (
              <span
                style={{
                  backgroundColor: "#E7592C",
                  color: "#fff",
                  borderRadius: "50%",
                  width: 22,
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {cartQuantity}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#f5f5f7",
              border: "none",
              borderRadius: "50%",
              width: 36,
              height: 36,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body (scrollable) */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 20px" }}>
          {loading ? (
            <div
              style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
            >
              <div className="spinner-border text-primary" role="status" style={{ width: 36, height: 36 }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : recaporder.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "60%",
                gap: 16,
                color: "#aaa",
              }}
            >
              <ShoppingCart size={56} strokeWidth={1} />
              <p style={{ fontSize: 16, margin: 0, fontWeight: 500 }}>Votre panier est vide</p>
              <p style={{ fontSize: 13, margin: 0, color: "#bbb" }}>
                Ajoutez des articles pour commencer
              </p>
            </div>
          ) : (
            <div style={{ paddingBottom: 8 }}>
              {recaporder.map((item, index) => {
                // Calculate complement costs respecting group-level nbre_gratuit * item.quantity.
                // 1. Group complements by complement_group_id
                const complementsByGroup = {};
                (item.complements || []).forEach((c) => {
                  const groupId = c.id_complement_group || c.groupe; // fallback to group name if id is missing
                  if (!complementsByGroup[groupId]) {
                    complementsByGroup[groupId] = {
                      nbre_gratuit: c.nbre_gratuit || 0,
                      items: []
                    };
                  }
                  complementsByGroup[groupId].items.push({ ...c });
                });

                // 2. Compute cost for each group by applying group-level free count (nbre_gratuit * main_item_quantity)
                // Sort items inside each group by price descending so that we give the free slots to the most expensive ones first,
                // and charge for the remaining ones.
                let complementsAdditional = 0;
                const billedComplementsMap = {}; // key: complement unique key, value: paid quantity

                Object.keys(complementsByGroup).forEach((groupId) => {
                  const group = complementsByGroup[groupId];
                  const totalFreeSlots = group.nbre_gratuit * item.quantity;
                  
                  // Sort items by price descending
                  group.items.sort((a, b) => b.prix - a.prix);
                  
                  let freeSlotsRemaining = totalFreeSlots;
                  group.items.forEach((c) => {
                    let qty = c.quantite;
                    let freeUsed = Math.min(qty, freeSlotsRemaining);
                    let paidQty = qty - freeUsed;
                    freeSlotsRemaining -= freeUsed;
                    
                    const compKey = `${c.nom}_${c.groupe}`;
                    billedComplementsMap[compKey] = paidQty;
                    complementsAdditional += paidQty * c.prix;
                  });
                });

                const totalPrice = (item.prix * item.quantity) + complementsAdditional;

                return (
                  <div
                    key={item.id_details || index}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      padding: "16px 0",
                      borderBottom: index < recaporder.length - 1 ? "1px solid #f5f5f7" : "none",
                    }}
                  >
                    {/* Image */}
                    <div
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: 10,
                        backgroundImage: item.image_url ? `url(${item.image_url})` : "none",
                        backgroundColor: "#f0f0f0",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        flexShrink: 0,
                      }}
                    />

                    {/* Center: name + unit price + complements + quantity badge */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{
                        margin: "0 0 1px 0",
                        fontSize: 15,
                        fontWeight: 600,
                        color: "#111",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {item.title}
                      </p>
                      <p style={{ margin: "0 0 6px 0", fontSize: 12, color: "#999" }}>
                        {item.prix} {devise} / unité
                      </p>

                      {/* Complements details */}
                      {item.complements && item.complements.length > 0 && (
                        <div style={{
                          marginBottom: 10,
                          backgroundColor: "#FAF8F7",
                          borderRadius: 10,
                          padding: "8px 10px",
                          border: "1px solid rgba(231,89,44,0.06)",
                        }}>
                          {/* Complement pills container */}
                          <div style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px 4px",
                          }}>
                            {item.complements
                              .slice(0, expandedItems[item.id_details] ? item.complements.length : 2)
                              .map((comp, ci) => {
                                const compKey = `${comp.nom}_${comp.groupe}`;
                                const paidQty = billedComplementsMap[compKey] || 0;
                                const extra = paidQty * comp.prix;
                                return (
                                  <div
                                    key={ci}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 4,
                                      backgroundColor: "#FFF4F0",
                                      border: "1px solid rgba(231,89,44,0.12)",
                                      borderRadius: 16,
                                      padding: "1px 7px 1px 5px",
                                    }}
                                  >
                                    <span style={{
                                      fontSize: 10,
                                      fontStyle: "italic",
                                      color: "#B84A1E",
                                      fontWeight: 500,
                                    }}>
                                      {comp.groupe && (
                                        <span style={{
                                          fontSize: 8,
                                          fontStyle: "normal",
                                          fontWeight: 700,
                                          color: "#C4622A",
                                          textTransform: "uppercase",
                                          marginRight: 2,
                                        }}>
                                          {comp.groupe} ·{" "}
                                        </span>
                                      )}
                                      {comp.quantite > 1 ? `${comp.quantite}×` : ""}{comp.nom}
                                    </span>
                                    {extra > 0 && (
                                      <span style={{
                                        fontSize: 9,
                                        fontStyle: "italic",
                                        color: "#E7592C",
                                        fontWeight: 700,
                                      }}>
                                        +{extra.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} {devise}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                          </div>

                          {/* Toggle Expand Button */}
                          {item.complements.length > 2 && (
                            <button
                              onClick={() => setExpandedItems(prev => ({
                                ...prev,
                                [item.id_details]: !prev[item.id_details]
                              }))}
                              style={{
                                background: "none",
                                border: "none",
                                padding: "4px 0 0 0",
                                color: "#E7592C",
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                marginTop: 6,
                              }}
                            >
                              {expandedItems[item.id_details] ? (
                                <>
                                  Masquer les compléments <ChevronUp size={12} />
                                </>
                              ) : (
                                <>
                                  Afficher les {item.complements.length - 2} autres compléments <ChevronDown size={12} />
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}

                      {item.commentaire && item.commentaire.trim() !== "" && (
                        <p style={{
                          margin: "0 0 6px 0",
                          fontSize: 11,
                          color: "#FF6A3D",
                          fontStyle: "italic",
                          whiteSpace: "pre-line",
                          lineHeight: "1.3"
                        }}>
                          {item.commentaire}
                        </p>
                      )}

                      {/* Quantity badge (read-only) */}
                      <div style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        backgroundColor: "#f5f5f7",
                        borderRadius: 20,
                        padding: "4px 12px",
                      }}>
                        <span style={{ fontSize: 13, color: "#888" }}>Qté</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>
                          {item.quantity}
                        </span>
                      </div>
                    </div>

                    {/* Right: total price + trash */}
                    <div style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      justifyContent: "space-between",
                      minHeight: 64,
                      flexShrink: 0,
                    }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>
                        {totalPrice.toLocaleString("fr-FR", {
                          minimumFractionDigits: 2, maximumFractionDigits: 2,
                        })}{" "}{devise}
                      </span>
                      <button
                        onClick={() => handleDelete(item.id_article)}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "#ccc", padding: 2,
                          display: "flex", alignItems: "center",
                          transition: "color 0.2s",
                          marginTop: 12,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#E7592C")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "#ccc")}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {recaporder.length > 0 && (
          <div
            style={{
              padding: "16px 20px 28px",
              borderTop: "1px solid #f0f0f0",
              flexShrink: 0,
              backgroundColor: "#fff",
            }}
          >
            {/* Total */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 600, color: "#666" }}>Total</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: "#001b44" }}>
                {calculateTotal().toLocaleString("fr-FR", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })}{" "}
                {devise}
              </span>
            </div>

            {/* Checkout button */}
            <button
              onClick={handleCheckout}
              style={{
                width: "100%",
                padding: "15px 20px",
                backgroundColor: "#E7592C",
                color: "#fff",
                border: "none",
                borderRadius: 14,
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 6px 20px rgba(231,89,44,0.35)",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(231,89,44,0.45)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(231,89,44,0.35)";
              }}
            >
              <span>Passer au paiement</span>
              <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

CartSidePanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  restauId: PropTypes.string,
  Table: PropTypes.string,
  onCartUpdate: PropTypes.func,
};

export default CartSidePanel;
